import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Spin, Space, Form, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { fetchSqlData } from '../../../../../services/api';
import type { QaqcPipeInspectionRecord } from '../../../../../types';
import PipeInspectionForm from './components/PipeInspectionForm';
import type { FileUploadToolHandle } from '../../../../../components/common/FileUploadTool';

const { Text } = Typography;

const QaqcPipeInspectionView: React.FC = () => {
  const navigate = useNavigate();
  const { report_id } = useParams<{ report_id: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState<QaqcPipeInspectionRecord | null>(null);
  const fileUploadRef = useRef<FileUploadToolHandle>(null);
  const [uploadPath, setUploadPath] = useState<string>('');
  const [weldMetaMap, setWeldMetaMap] = useState<Record<string, {
    pipe_size?: string | null,
    service_class_name?: string | null,
    weldtype_name?: string | null,
    spool_id?: number | null,
    weld_category?: string | null,
    code_no?: string | null,
    fluid?: string | null,
  }>>({});

  const weldRevMap = useMemo(() => {
    const map: Record<string, number | null> = {};
    const ids = (row as any)?.weldingdata_ids as (string | number)[] | undefined;
    const revs = (row as any)?.weld_no_revs as (number | null)[] | undefined;
    if (Array.isArray(ids) && Array.isArray(revs)) {
      ids.forEach((id, idx) => { map[String(id)] = revs[idx] ?? null; });
    }
    return map;
  }, [row]);

  useEffect(() => {
    const load = async () => {
      if (!report_id) return;
      setLoading(true);
      try {
        const res = await fetchSqlData('qaqc_pipe_inspection_records', 'report_id', report_id);
        const r: QaqcPipeInspectionRecord | undefined = Array.isArray((res as any)?.data)
          ? (res as any).data?.[0]
          : (Array.isArray(res) ? (res as any)[0] : undefined);
        if (!r) return;
        setRow(r);
        setUploadPath(r.upload_path || '');
        form.setFieldsValue({
          report_id: r.report_id,
          issued_by: r.issued_by || '',
          report_date: r.report_date ? dayjs(r.report_date) : undefined,
          report_notes: r.report_notes || '',
          rfi_no: (r as any)?.rfi_id ? String((r as any).rfi_id) : undefined,
          work_permit_ref_num: (r as any)?.work_permit_ref_num || undefined,
          work_permit_description: (r as any)?.work_permit_description || undefined,
          attendance: Array.isArray((r as any)?.attendees) ? (r as any).attendees : [],
          attendee_ids: (r as any)?.attendee_ids || undefined,
        });
        // Fetch details and map payloads, also build weld meta (including fluid)
        try {
          const det = await fetchSqlData('qaqc_pipe_insp_rcrd_details', 'inspection_report_id', r.report_id);
          const detArr: any[] = Array.isArray((det as any)?.data) ? (det as any).data : (Array.isArray(det) ? (det as any) : []);
          const meta: Record<string, any> = {};
          (detArr || []).forEach((d: any) => {
            const k = String(d.weldingdata_id);
            meta[k] = {
              pipe_size: d.pipe_size ?? null,
              service_class_name: d.service_class_name ?? null,
              weldtype_name: d.weldtype_name ?? null,
              spool_id: ((): number | null => {
                const v = d.spool_id; if (v === null || v === undefined || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null;
              })(),
              weld_category: d.weld_category ?? null,
              code_no: d.code_no ?? null,
            };
          });
          try {
            const rfiFormId = (r as any)?.rfi_id;
            if (rfiFormId) {
              const rfiRes = await fetchSqlData('qaqc_pipe_rfi_forms', 'form_id', rfiFormId);
              const rfiRow = Array.isArray((rfiRes as any)?.data) ? (rfiRes as any).data?.[0] : (Array.isArray(rfiRes) ? (rfiRes as any)[0] : undefined);
              if (rfiRow) {
                const ids: string[] = Array.isArray(rfiRow.weldingdata_ids) ? rfiRow.weldingdata_ids : [];
                const fluids: (string | null)[] = Array.isArray((rfiRow as any).fluids) ? (rfiRow as any).fluids : [];
                ids.forEach((wid: string, idx: number) => {
                  const k = String(wid);
                  const f = fluids[idx] ?? null;
                  meta[k] = { ...(meta[k] || {}), fluid: f != null ? String(f) : null };
                });
              }
            }
          } catch {}
          setWeldMetaMap(meta);
          const toBool = (v: any) => (typeof v === 'string' ? v.toLowerCase() === 'true' : Boolean(v));
          const toNum = (v: any) => {
            if (v === '' || v === null || v === undefined) return undefined;
            const n = Number(v); return Number.isFinite(n) ? n : undefined;
          };
          const results: any = {};
          (detArr || []).forEach((d: any) => {
            const wid = String(d.weldingdata_id);
            let payload: any = {};
            try { payload = typeof d.payload === 'string' ? JSON.parse(d.payload) : (d.payload || {}); } catch {}
            const out: any = {};
            if (payload.result) out.result = String(payload.result);
            if (payload.hardness != null) out.hardness = toNum(payload.hardness);
            if (payload.bc) {
              const bcSrc = payload.bc || {};
              const isFlat = !(bcSrc.fitup || bcSrc.surface_ok || bcSrc.tack_welds_ok || bcSrc.consumables_env);
              const fitup = isFlat
                ? { hi_lo_mm: toNum(bcSrc.hi_lo_mm), ovality_ok: bcSrc.ovality_ok !== undefined ? toBool(bcSrc.ovality_ok) : undefined }
                : { hi_lo_mm: toNum(bcSrc?.fitup?.hi_lo_mm), ovality_ok: bcSrc?.fitup?.ovality_ok !== undefined ? toBool(bcSrc?.fitup?.ovality_ok) : undefined };
              const surface_ok = isFlat
                ? { cracks: toBool(bcSrc.cracks), laminations: toBool(bcSrc.laminations), rust: toBool(bcSrc.rust), paint_oil: toBool(bcSrc.paint_oil) }
                : { cracks: toBool(bcSrc?.surface_ok?.cracks), laminations: toBool(bcSrc?.surface_ok?.laminations), rust: toBool(bcSrc?.surface_ok?.rust), paint_oil: toBool(bcSrc?.surface_ok?.paint_oil) };
              const tack_welds_ok = isFlat
                ? { size_spacing: toBool(bcSrc.size_spacing), defects_free: toBool(bcSrc.defects_free) }
                : { size_spacing: toBool(bcSrc?.tack_welds_ok?.size_spacing), defects_free: toBool(bcSrc?.tack_welds_ok?.defects_free) };
              const consumables_env = isFlat
                ? { electrode_batch_ok: toBool(bcSrc.electrode_batch_ok), preheat_ok: toBool(bcSrc.preheat_ok), preheat_temp: toNum(bcSrc.preheat_temp), ambient_ok: toBool(bcSrc.ambient_ok) }
                : { electrode_batch_ok: toBool(bcSrc?.consumables_env?.electrode_batch_ok), preheat_ok: toBool(bcSrc?.consumables_env?.preheat_ok), preheat_temp: toNum(bcSrc?.consumables_env?.preheat_temp), ambient_ok: toBool(bcSrc?.consumables_env?.ambient_ok) };
              out.bc = {
                bevel_angle: toNum(bcSrc.bevel_angle),
                root_face_mm: toNum(bcSrc.root_face_mm),
                root_gap_mm: toNum(bcSrc.root_gap_mm),
                heat_no_1: bcSrc.heat_no_1 ?? undefined,
                heat_no_2: bcSrc.heat_no_2 ?? undefined,
                fitup,
                prep_method: bcSrc.prep_method ?? undefined,
                cleaning_ok: bcSrc.cleaning_ok !== undefined ? toBool(bcSrc.cleaning_ok) : undefined,
                surface_ok,
                tack_welds_ok,
                consumables_env,
                remarks: bcSrc.remarks ?? undefined,
              };
            }
            if (payload.ut)     {
              const utSrc = payload.ut || {} as any;
              const toStr = (v: any) => (v === '' || v == null ? undefined : String(v));
              const toBool = (v: any) => (v === undefined ? undefined : (typeof v === 'string' ? v.toLowerCase() === 'true' : Boolean(v)));
              const outUt: any = {
                equipment: toStr(utSrc.equipment),
                probe_type: toStr(utSrc.probe_type),
                frequency_mhz: toStr(utSrc.frequency_mhz),
                probe_angle: toStr(utSrc.probe_angle),
                calibration_block: toStr(utSrc.calibration_block),
                couplant_type: toStr(utSrc.couplant_type),
                scan_method: toStr(utSrc.scan_method),
                scan_coverage: toStr(utSrc.scan_coverage),
                coverage_percent: toStr(utSrc.coverage_percent),
                sensitivity_mode: toStr(utSrc.sensitivity_mode),
                sensitivity_db: toStr(utSrc.sensitivity_db),
                range_value: toStr(utSrc.range_value),
                range_unit: toStr(utSrc.range_unit),
                sweep_value: toStr(utSrc.sweep_value),
                sweep_unit: toStr(utSrc.sweep_unit),
                gate_start: toStr(utSrc.gate_start),
                gate_width: toStr(utSrc.gate_width),
                gate_threshold_percent: toStr(utSrc.gate_threshold_percent),
                indications: {
                  no_cracks: toBool(utSrc.no_cracks ?? utSrc?.indications?.no_cracks),
                  proper_fusion: toBool(utSrc.proper_fusion ?? utSrc?.indications?.proper_fusion),
                  no_porosity: toBool(utSrc.no_porosity ?? utSrc?.indications?.no_porosity),
                  no_laps: toBool(utSrc.no_laps ?? utSrc?.indications?.no_laps),
                },
                remarks: toStr(utSrc.remarks),
              };
              out.ut = outUt;
            }
            if (payload.mt) {
              const mtSrc = payload.mt || {};
              const isFlatMt = !(
                mtSrc.preparation || mtSrc.current || mtSrc.field_strength || mtSrc.particle || mtSrc.indications || mtSrc.finish
              );
              const preparation = {
                clean: (isFlatMt ? mtSrc.clean : mtSrc?.preparation?.clean) !== undefined
                  ? Boolean(isFlatMt ? (typeof mtSrc.clean === 'string' ? mtSrc.clean?.toLowerCase?.() === 'true' : mtSrc.clean) : (typeof mtSrc?.preparation?.clean === 'string' ? mtSrc?.preparation?.clean?.toLowerCase?.() === 'true' : mtSrc?.preparation?.clean))
                  : undefined,
                coating_removed: (isFlatMt ? mtSrc.coating_removed : mtSrc?.preparation?.coating_removed) !== undefined
                  ? Boolean(isFlatMt ? (typeof mtSrc.coating_removed === 'string' ? mtSrc.coating_removed?.toLowerCase?.() === 'true' : mtSrc.coating_removed) : (typeof mtSrc?.preparation?.coating_removed === 'string' ? mtSrc?.preparation?.coating_removed?.toLowerCase?.() === 'true' : mtSrc?.preparation?.coating_removed))
                  : undefined,
              } as any;
              const method = mtSrc.method ?? undefined;
              const current = {
                type: isFlatMt ? (mtSrc.current_type ?? undefined) : (mtSrc?.current?.type ?? undefined),
                amperes: isFlatMt ? (mtSrc.current_amperes ?? undefined) : (mtSrc?.current?.amperes ?? undefined),
              } as any;
              const lift_test = mtSrc.lift_test ?? undefined;
              const field_strength = {
                flux_density: isFlatMt ? (mtSrc.flux_density ?? undefined) : (mtSrc?.field_strength?.flux_density ?? undefined),
                units: isFlatMt ? (mtSrc.field_units ?? undefined) : (mtSrc?.field_strength?.units ?? undefined),
              } as any;
              const particle = {
                type: isFlatMt ? (mtSrc.particle_type ?? undefined) : (mtSrc?.particle?.type ?? undefined),
                brand_batch: isFlatMt ? (mtSrc.particle_brand_batch ?? undefined) : (mtSrc?.particle?.brand_batch ?? undefined),
                particle_applied: (isFlatMt ? mtSrc.particle_applied : mtSrc?.particle?.particle_applied) !== undefined
                  ? Boolean(isFlatMt ? (typeof mtSrc.particle_applied === 'string' ? mtSrc.particle_applied?.toLowerCase?.() === 'true' : mtSrc.particle_applied) : (typeof mtSrc?.particle?.particle_applied === 'string' ? mtSrc?.particle?.particle_applied?.toLowerCase?.() === 'true' : mtSrc?.particle?.particle_applied))
                  : undefined,
              } as any;
              const lighting = mtSrc.lighting ?? undefined;
              const lighting_unit = (() => {
                const t = isFlatMt ? (mtSrc.particle_type ?? undefined) : (mtSrc?.particle?.type ?? undefined);
                if (!t || t === 'na') return undefined;
                return t === 'wet_fluorescent' ? 'uv' : 'lux';
              })();
              const development_time_min = ((): number | undefined => {
                const v = mtSrc.development_time_min;
                if (v === '' || v === null || v === undefined) return undefined;
                const n = Number(v); return Number.isFinite(n) ? n : undefined;
              })();
              const indications = {
                no_cracks: (isFlatMt ? mtSrc.no_cracks : mtSrc?.indications?.no_cracks) !== undefined
                  ? Boolean(isFlatMt ? (typeof mtSrc.no_cracks === 'string' ? mtSrc.no_cracks?.toLowerCase?.() === 'true' : mtSrc.no_cracks) : (typeof mtSrc?.indications?.no_cracks === 'string' ? mtSrc?.indications?.no_cracks?.toLowerCase?.() === 'true' : mtSrc?.indications?.no_cracks))
                  : undefined,
                proper_fusion: (isFlatMt ? mtSrc.proper_fusion : mtSrc?.indications?.proper_fusion) !== undefined
                  ? Boolean(isFlatMt ? (typeof mtSrc.proper_fusion === 'string' ? mtSrc.proper_fusion?.toLowerCase?.() === 'true' : mtSrc.proper_fusion) : (typeof mtSrc?.indications?.proper_fusion === 'string' ? mtSrc?.indications?.proper_fusion?.toLowerCase?.() === 'true' : mtSrc?.indications?.proper_fusion))
                  : undefined,
                no_porosity: (isFlatMt ? mtSrc.no_porosity : mtSrc?.indications?.no_porosity) !== undefined
                  ? Boolean(isFlatMt ? (typeof mtSrc.no_porosity === 'string' ? mtSrc.no_porosity?.toLowerCase?.() === 'true' : mtSrc.no_porosity) : (typeof mtSrc?.indications?.no_porosity === 'string' ? mtSrc?.indications?.no_porosity?.toLowerCase?.() === 'true' : mtSrc?.indications?.no_porosity))
                  : undefined,
                no_laps: (isFlatMt ? mtSrc.no_laps : mtSrc?.indications?.no_laps) !== undefined
                  ? Boolean(isFlatMt ? (typeof mtSrc.no_laps === 'string' ? mtSrc.no_laps?.toLowerCase?.() === 'true' : mtSrc.no_laps) : (typeof mtSrc?.indications?.no_laps === 'string' ? mtSrc?.indications?.no_laps?.toLowerCase?.() === 'true' : mtSrc?.indications?.no_laps))
                  : undefined,
              } as any;
              const finish = {
                post_cleaning: (isFlatMt ? mtSrc.post_cleaning : mtSrc?.finish?.post_cleaning) !== undefined
                  ? Boolean(isFlatMt ? (typeof mtSrc.post_cleaning === 'string' ? mtSrc.post_cleaning?.toLowerCase?.() === 'true' : mtSrc.post_cleaning) : (typeof mtSrc?.finish?.post_cleaning === 'string' ? mtSrc?.finish?.post_cleaning?.toLowerCase?.() === 'true' : mtSrc?.finish?.post_cleaning))
                  : undefined,
              } as any;
              out.mt = {
                preparation,
                method,
                current,
                lift_test,
                field_strength,
                particle,
                lighting,
                lighting_unit,
                development_time_min,
                indications,
                finish,
                remarks: mtSrc.remarks ?? undefined,
              };
            }
            if (payload.pt) {
              const ptSrc = payload.pt || {};
              const isFlatPt = !(
                ptSrc.preparation || ptSrc.application || ptSrc.cleaning || ptSrc.developer || ptSrc.indications || ptSrc.finish
              );
              const preparation = {
                surface_cleaned: (isFlatPt ? ptSrc.surface_cleaned : ptSrc?.preparation?.surface_cleaned) !== undefined
                  ? Boolean(isFlatPt ? (typeof ptSrc.surface_cleaned === 'string' ? ptSrc.surface_cleaned?.toLowerCase?.() === 'true' : ptSrc.surface_cleaned) : (typeof ptSrc?.preparation?.surface_cleaned === 'string' ? ptSrc?.preparation?.surface_cleaned?.toLowerCase?.() === 'true' : ptSrc?.preparation?.surface_cleaned))
                  : undefined,
              } as any;
              const application = {
                penetrant_type: isFlatPt ? (ptSrc.penetrant_type ?? undefined) : (ptSrc?.application?.penetrant_type ?? undefined),
                dwell_time_min: ((): number | undefined => {
                  const v = isFlatPt ? ptSrc.dwell_time_min : ptSrc?.application?.dwell_time_min;
                  if (v === '' || v === null || v === undefined) return undefined;
                  const n = Number(v); return Number.isFinite(n) ? n : undefined;
                })(),
                surface_cleaned_no_residue: ((): boolean | undefined => {
                  const val = isFlatPt ? ptSrc.surface_cleaned_no_residue : (ptSrc?.application?.surface_cleaned_no_residue ?? ptSrc?.cleaning?.surface_cleaned_no_residue);
                  if (val === undefined) return undefined;
                  if (typeof val === 'string') return val.toLowerCase() === 'true';
                  return Boolean(val);
                })(),
              } as any;
              const developer = {
                type: isFlatPt ? (ptSrc.type ?? undefined) : (ptSrc?.developer?.type ?? undefined),
                development_time_min: ((): number | undefined => {
                  const v = isFlatPt ? ptSrc.development_time_min : ptSrc?.developer?.development_time_min;
                  if (v === '' || v === null || v === undefined) return undefined;
                  const n = Number(v); return Number.isFinite(n) ? n : undefined;
                })(),
              } as any;
              const indications = {
                no_cracks: (isFlatPt ? ptSrc.no_cracks : ptSrc?.indications?.no_cracks) !== undefined
                  ? Boolean(isFlatPt ? (typeof ptSrc.no_cracks === 'string' ? ptSrc.no_cracks?.toLowerCase?.() === 'true' : ptSrc.no_cracks) : (typeof ptSrc?.indications?.no_cracks === 'string' ? ptSrc?.indications?.no_cracks?.toLowerCase?.() === 'true' : ptSrc?.indications?.no_cracks))
                  : undefined,
                proper_fusion: (isFlatPt ? ptSrc.proper_fusion : ptSrc?.indications?.proper_fusion) !== undefined
                  ? Boolean(isFlatPt ? (typeof ptSrc.proper_fusion === 'string' ? ptSrc.proper_fusion?.toLowerCase?.() === 'true' : ptSrc.proper_fusion) : (typeof ptSrc?.indications?.proper_fusion === 'string' ? ptSrc?.indications?.proper_fusion?.toLowerCase?.() === 'true' : ptSrc?.indications?.proper_fusion))
                  : undefined,
                no_porosity: (isFlatPt ? ptSrc.no_porosity : ptSrc?.indications?.no_porosity) !== undefined
                  ? Boolean(isFlatPt ? (typeof ptSrc.no_porosity === 'string' ? ptSrc.no_porosity?.toLowerCase?.() === 'true' : ptSrc.no_porosity) : (typeof ptSrc?.indications?.no_porosity === 'string' ? ptSrc?.indications?.no_porosity?.toLowerCase?.() === 'true' : ptSrc?.indications?.no_porosity))
                  : undefined,
                no_laps: (isFlatPt ? ptSrc.no_laps : ptSrc?.indications?.no_laps) !== undefined
                  ? Boolean(isFlatPt ? (typeof ptSrc.no_laps === 'string' ? ptSrc.no_laps?.toLowerCase?.() === 'true' : ptSrc.no_laps) : (typeof ptSrc?.indications?.no_laps === 'string' ? ptSrc?.indications?.no_laps?.toLowerCase?.() === 'true' : ptSrc?.indications?.no_laps))
                  : undefined,
              } as any;
              const finish = {
                post_cleaning: (isFlatPt ? ptSrc.post_cleaning : ptSrc?.finish?.post_cleaning) !== undefined
                  ? Boolean(isFlatPt ? (typeof ptSrc.post_cleaning === 'string' ? ptSrc.post_cleaning?.toLowerCase?.() === 'true' : ptSrc.post_cleaning) : (typeof ptSrc?.finish?.post_cleaning === 'string' ? ptSrc?.finish?.post_cleaning?.toLowerCase?.() === 'true' : ptSrc?.finish?.post_cleaning))
                  : undefined,
              } as any;
              out.pt = {
                preparation,
                application,
                developer,
                indications,
                finish,
                remarks: ptSrc.remarks ?? undefined,
              };
            }
            if (payload.vc) {
              const vcSrc = payload.vc || {};
              const isFlatVc = !(
                vcSrc.external_profile_dimensions || vcSrc.surface_discontinuities || vcSrc.root_penetration ||
                vcSrc.cleanliness_contaminants || vcSrc.haz_thermal_effects || vcSrc.distortion_alignment || vcSrc.finish_markings
              );
              const external_profile_dimensions = {
                weld_size_ok: (isFlatVc ? vcSrc.weld_size_ok : vcSrc?.external_profile_dimensions?.weld_size_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.weld_size_ok : vcSrc?.external_profile_dimensions?.weld_size_ok)
                  : undefined,
                reinforcement_ok: (isFlatVc ? vcSrc.reinforcement_ok : vcSrc?.external_profile_dimensions?.reinforcement_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.reinforcement_ok : vcSrc?.external_profile_dimensions?.reinforcement_ok)
                  : undefined,
                contour_ok: (isFlatVc ? vcSrc.contour_ok : vcSrc?.external_profile_dimensions?.contour_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.contour_ok : vcSrc?.external_profile_dimensions?.contour_ok)
                  : undefined,
                undersize_oversize_ok: (isFlatVc ? vcSrc.undersize_oversize_ok : vcSrc?.external_profile_dimensions?.undersize_oversize_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.undersize_oversize_ok : vcSrc?.external_profile_dimensions?.undersize_oversize_ok)
                  : undefined,
              };
              const surface_discontinuities = {
                no_cracks: (isFlatVc ? vcSrc.no_cracks : vcSrc?.surface_discontinuities?.no_cracks) !== undefined
                  ? toBool(isFlatVc ? vcSrc.no_cracks : vcSrc?.surface_discontinuities?.no_cracks)
                  : undefined,
                no_porosity: (isFlatVc ? vcSrc.no_porosity : vcSrc?.surface_discontinuities?.no_porosity) !== undefined
                  ? toBool(isFlatVc ? vcSrc.no_porosity : vcSrc?.surface_discontinuities?.no_porosity)
                  : undefined,
                undercut_ok: (isFlatVc ? vcSrc.undercut_ok : vcSrc?.surface_discontinuities?.undercut_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.undercut_ok : vcSrc?.surface_discontinuities?.undercut_ok)
                  : undefined,
                no_overlap: (isFlatVc ? vcSrc.no_overlap : vcSrc?.surface_discontinuities?.no_overlap) !== undefined
                  ? toBool(isFlatVc ? vcSrc.no_overlap : vcSrc?.surface_discontinuities?.no_overlap)
                  : undefined,
                fusion_fill_ok: (isFlatVc ? vcSrc.fusion_fill_ok : vcSrc?.surface_discontinuities?.fusion_fill_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.fusion_fill_ok : vcSrc?.surface_discontinuities?.fusion_fill_ok)
                  : undefined,
              };
              const root_penetration = {
                root_condition_ok: (isFlatVc ? vcSrc.root_condition_ok : vcSrc?.root_penetration?.root_condition_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.root_condition_ok : vcSrc?.root_penetration?.root_condition_ok)
                  : undefined,
                penetration_ok: (isFlatVc ? vcSrc.penetration_ok : vcSrc?.root_penetration?.penetration_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.penetration_ok : vcSrc?.root_penetration?.penetration_ok)
                  : undefined,
                root_reinforcement_ok: (isFlatVc ? vcSrc.root_reinforcement_ok : vcSrc?.root_penetration?.root_reinforcement_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.root_reinforcement_ok : vcSrc?.root_penetration?.root_reinforcement_ok)
                  : undefined,
              };
              const cleanliness_contaminants = {
                slag_spatter_removed: (isFlatVc ? vcSrc.slag_spatter_removed : vcSrc?.cleanliness_contaminants?.slag_spatter_removed) !== undefined
                  ? toBool(isFlatVc ? vcSrc.slag_spatter_removed : vcSrc?.cleanliness_contaminants?.slag_spatter_removed)
                  : undefined,
                no_foreign_inclusions: (isFlatVc ? vcSrc.no_foreign_inclusions : vcSrc?.cleanliness_contaminants?.no_foreign_inclusions) !== undefined
                  ? toBool(isFlatVc ? vcSrc.no_foreign_inclusions : vcSrc?.cleanliness_contaminants?.no_foreign_inclusions)
                  : undefined,
                toes_adjacent_clean: (isFlatVc ? vcSrc.toes_adjacent_clean : vcSrc?.cleanliness_contaminants?.toes_adjacent_clean) !== undefined
                  ? toBool(isFlatVc ? vcSrc.toes_adjacent_clean : vcSrc?.cleanliness_contaminants?.toes_adjacent_clean)
                  : undefined,
              };
              const haz_thermal_effects = {
                discoloration_ok: (isFlatVc ? vcSrc.discoloration_ok : vcSrc?.haz_thermal_effects?.discoloration_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.discoloration_ok : vcSrc?.haz_thermal_effects?.discoloration_ok)
                  : undefined,
                no_haz_cracking: (isFlatVc ? vcSrc.no_haz_cracking : vcSrc?.haz_thermal_effects?.no_haz_cracking) !== undefined
                  ? toBool(isFlatVc ? vcSrc.no_haz_cracking : vcSrc?.haz_thermal_effects?.no_haz_cracking)
                  : undefined,
                coating_damage_checked: (isFlatVc ? vcSrc.coating_damage_checked : vcSrc?.haz_thermal_effects?.coating_damage_checked) !== undefined
                  ? toBool(isFlatVc ? vcSrc.coating_damage_checked : vcSrc?.haz_thermal_effects?.coating_damage_checked)
                  : undefined,
              };
              const distortion_alignment = {
                misalignment_ok: (isFlatVc ? vcSrc.misalignment_ok : vcSrc?.distortion_alignment?.misalignment_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.misalignment_ok : vcSrc?.distortion_alignment?.misalignment_ok)
                  : undefined,
                no_twist_bow: (isFlatVc ? vcSrc.no_twist_bow : vcSrc?.distortion_alignment?.no_twist_bow) !== undefined
                  ? toBool(isFlatVc ? vcSrc.no_twist_bow : vcSrc?.distortion_alignment?.no_twist_bow)
                  : undefined,
                dimensional_checks_ok: (isFlatVc ? vcSrc.dimensional_checks_ok : vcSrc?.distortion_alignment?.dimensional_checks_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.dimensional_checks_ok : vcSrc?.distortion_alignment?.dimensional_checks_ok)
                  : undefined,
              };
              const finish_markings = {
                smooth_transition_tack_ok: (isFlatVc ? vcSrc.smooth_transition_tack_ok : vcSrc?.finish_markings?.smooth_transition_tack_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.smooth_transition_tack_ok : vcSrc?.finish_markings?.smooth_transition_tack_ok)
                  : undefined,
                spatter_ok: (isFlatVc ? vcSrc.spatter_ok : vcSrc?.finish_markings?.spatter_ok) !== undefined
                  ? toBool(isFlatVc ? vcSrc.spatter_ok : vcSrc?.finish_markings?.spatter_ok)
                  : undefined,
              };
              out.vc = {
                external_profile_dimensions,
                surface_discontinuities,
                root_penetration,
                cleanliness_contaminants,
                haz_thermal_effects,
                distortion_alignment,
                finish_markings,
                remarks: vcSrc.remarks ?? undefined,
                flw_welder: ((): string | undefined => {
                  const s = vcSrc.flw_welder;
                  return s === '' || s == null ? undefined : String(s);
                })(),
                fw_welder: ((): string | undefined => {
                  const s = vcSrc.fw_welder;
                  return s === '' || s == null ? undefined : String(s);
                })(),
                flw_date: ((): any => {
                  const s = vcSrc.flw_date;
                  return s === '' || s == null ? undefined : dayjs(String(s));
                })(),
                fw_date: ((): any => {
                  const s = vcSrc.fw_date;
                  return s === '' || s == null ? undefined : dayjs(String(s));
                })(),
              };
            }
            results[wid] = { ...(results[wid] || {}), ...out };
          });
          if (Array.isArray(r.weldingdata_ids)) {
            r.weldingdata_ids.forEach((w: any) => { const k = String(w); results[k] = results[k] || {}; });
          }
          // VC fallback: fill missing welders/dates from header arrays by index
          try {
            if ((r as any)?.inspection_type && String((r as any).inspection_type).toUpperCase() === 'VC') {
              const ids: string[] = Array.isArray(r.weldingdata_ids) ? r.weldingdata_ids.map((x: any) => String(x)) : [];
              const flwA: (string | null)[] = Array.isArray((r as any).flw_welders) ? (r as any).flw_welders : [];
              const fwA: (string | null)[] = Array.isArray((r as any).fw_welders) ? (r as any).fw_welders : [];
              const flwD: (string | null)[] = Array.isArray((r as any).flw_dates) ? (r as any).flw_dates : [];
              const fwD: (string | null)[] = Array.isArray((r as any).fw_dates) ? (r as any).fw_dates : [];
              ids.forEach((wid, idx) => {
                const cur = results[wid] || {};
                const curVc = cur.vc || {};
                const s1 = curVc.flw_welder;
                const s2 = curVc.fw_welder;
                const v1 = (s1 === undefined || s1 === null || String(s1).trim() === '') ? (flwA[idx] != null ? String(flwA[idx]) : undefined) : s1;
                const v2 = (s2 === undefined || s2 === null || String(s2).trim() === '') ? (fwA[idx] != null ? String(fwA[idx]) : undefined) : s2;
                const d1 = curVc.flw_date;
                const d2 = curVc.fw_date;
                const dv1 = (d1 === undefined || d1 === null || String(d1).trim?.() === '') ? (flwD[idx] ? dayjs(String(flwD[idx])) : undefined) : d1;
                const dv2 = (d2 === undefined || d2 === null || String(d2).trim?.() === '') ? (fwD[idx] ? dayjs(String(fwD[idx])) : undefined) : d2;
                results[wid] = { ...cur, vc: { ...curVc, flw_welder: v1, fw_welder: v2, flw_date: dv1, fw_date: dv2 } };
              });
            }
          } catch {}
          try { form.resetFields(['results']); } catch {}
          setTimeout(() => {
            try { form.setFieldsValue({ results }); } catch {}
          }, 0);
        } catch {}
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [report_id, form]);

  return (
    <Card title={<span><a onClick={() => navigate(-1)} style={{ marginRight: 16, fontSize: 16 }}>&larr; Back</a>View Pipe Inspection{row?.report_id ? ` — ${row.report_id}` : ''}</span>} style={{ maxWidth: 800, margin: '0 auto' }}>
      <Spin spinning={loading}>
        {row && (
          <Form form={form} layout="vertical" size="small">
            <PipeInspectionForm
              form={form}
              mode="view"
              inspectionType={row?.inspection_type}
              weldingdataIds={row?.weldingdata_ids || []}
              weldRevMap={weldRevMap}
              fileUploadRef={fileUploadRef}
              uploadPath={uploadPath}
            />
          </Form>
        )}
      </Spin>
    </Card>
  );
};

export default QaqcPipeInspectionView;
